import cx from "classnames";
import React from "react";

import * as styles from "./FormattedText.module.css";

const boldStart = "*>";
const boldEnd = "<";
const breakline = "<br/>";
const linkStart = "<link";
const linkEnd = "/>";

type FormattedTextProps = {
  text: string;
  className?: string;
};

type LinkInfo = {
  text: string;
  url: string;
  openInNewTab?: boolean;
};

export const FormattedText = (props: FormattedTextProps) => {
  let textWithBreaklines: string[] = [];

  const parseLink = (linkString: string): LinkInfo | null => {
    const textMatch = linkString.match(/text=['"]([^'"]*)['"]/);
    const urlMatch = linkString.match(/url=([^\s'">]+)/);
    const targetMatch = linkString.match(/target=['"]_blank['"]/);

    if (textMatch && urlMatch) {
      return {
        text: textMatch[1],
        url: urlMatch[1],
        openInNewTab: !!targetMatch,
      };
    }
    return null;
  };

  const traverseAndParseBreakLines = () => {
    // Split the description into an array by searching the breakline sintax.
    // Each position is a phrase.
    if (props.text) {
      textWithBreaklines = props.text.split(breakline);
    }
  };

  const processLineWithLinksAndBold = (line: string) => {
    // First, extract links and replace them with placeholders
    const linkRegex = new RegExp(`${linkStart}[^>]*${linkEnd}`, "g");
    const links = line.match(linkRegex) || [];
    const linkPlaceholders: string[] = [];
    let processedLine = line;

    // Replace links with placeholders
    links.forEach((link, index) => {
      const placeholder = `__LINK_${index}__`;
      linkPlaceholders.push(placeholder);
      processedLine = processedLine.replace(link, placeholder);
    });

    // Now process bold text on the line without links
    const boldProcessedParts = processedLine.split(boldStart);
    const boldPositions = new Set<number>();

    for (let i = 0; i < boldProcessedParts.length; i++) {
      const part = boldProcessedParts[i];
      if (part.includes(boldEnd)) {
        boldPositions.add(i);
        boldProcessedParts[i] = part.slice(0, -1); // Remove the bold end marker
      }
    }

    // Render the parts
    return boldProcessedParts.map((part, index) => {
      let content: React.ReactNode = part;

      // Replace link placeholders with actual links
      linkPlaceholders.forEach((placeholder, linkIndex) => {
        if (typeof content === "string" && content.includes(placeholder)) {
          const linkInfo = parseLink(links[linkIndex]);
          if (linkInfo) {
            const beforeLink = content.split(placeholder)[0];
            const afterLink = content.split(placeholder)[1];

            content = (
              <>
                {beforeLink}
                <a
                  href={linkInfo.url}
                  target={linkInfo.openInNewTab ? "_blank" : undefined}
                  rel={linkInfo.openInNewTab ? "noopener noreferrer" : undefined}
                >
                  {linkInfo.text}
                </a>
                {afterLink}
              </>
            );
          }
        }
      });

      return boldPositions.has(index) ? <b>{content}</b> : content;
    });
  };

  traverseAndParseBreakLines();

  return (
    <span className={cx(styles.text, props.className)}>
      {textWithBreaklines.map((line, lineIndex) => (
        <>
          {processLineWithLinksAndBold(line)}
          {lineIndex < textWithBreaklines.length - 1 && <br />}
        </>
      ))}
    </span>
  );
};
